import React, { useEffect, useMemo, useState } from "react";
import api, { fopSettingsApi, salesClientsApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Send,
  CheckCircle2,
  CreditCard,
  Trash2,
  ExternalLink,
  Globe,
  FileText,
  FileCheck,
  Building2,
  Plus,
  Landmark,
} from "lucide-react";

// Raw formatter — used by the FX `Saved as` tooltip where we show the
// untouched stored value alongside the display-currency conversion.
const fmtCurrencyRaw = (amount, currency = "EUR") => {
  if (amount == null) return "—";
  const sym = currency === "EUR" ? "€" : currency === "UAH" ? "₴" : "$";
  return `${sym}${Number(amount).toLocaleString()}`;
};

const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  if (!y || !mm) return m;
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mm, 10) - 1] || mm} ${y}`;
};

/**
 * Support payments table — mirrors the Sales-project payment row workflow:
 *  - Mark sent / unmark sent (locks invoice_sent_at + due date)
 *  - Download Invoice DOCX
 *  - Download Act DOCX
 *  - Send Payment Request to client (creates client_requests row of type
 *    "payment_request" — visible in client portal under "Payments due")
 *  - Stripe payment link
 *
 * Data source: GET /support-services?month=YYYY-MM (returns sales_services
 * rows from auto-created Support unified projects).
 *
 * Props:
 *  - selectedMonth: "YYYY-MM"
 *  - onNavigateMonth: (delta:int) => void
 *  - onGenerateMonth: () => Promise — triggers POST /crm/generate-month/{month}?category=support
 */
export default function SupportPaymentsView({
  selectedMonth,
  onNavigateMonth,
  onGenerateMonth,
  // Optional — when caller passes these, we render the "Add One-off" button
  // and use them to populate the dialog without an extra fetch.
  supportClients = [],
  // Client pool for the one-off charge picker. A one-off may target ANY managed
  // client, not only support-tagged ones, so the caller passes the broader
  // crm+support list here. Falls back to supportClients. (backlog 0755a82e)
  oneOffClients = [],
  supportTariffs = [],
  onAfterOneOff,
}) {
  const oneOffClientPool = (oneOffClients && oneOffClients.length) ? oneOffClients : supportClients;
  // Display currency comes from the sidebar switcher (USD/EUR/UAH). The DB
  // stores amounts in the client's invoice_currency (used by the DOCX
  // generator); we convert to display currency for the table via PrivatBank
  // rates exposed by CurrencyContext.
  const { formatAmount } = useCurrency();
  const fmtCurrency = (amount, currency = "EUR") => {
    if (amount == null) return "—";
    return formatAmount(amount, currency);
  };
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientRequests, setClientRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  // FOP catalog — used to resolve sales_service.legal_entity_id → human-readable
  // name in the Payments table. FOP itself is now picked at the client level
  // (Clients tab → Edit dialog) and propagated to each monthly bill at
  // Generate Month time.
  const [fops, setFops] = useState([]);
  // One-off payment dialog (pay-as-you-go tariff / ad-hoc support charges).
  const [oneOffOpen, setOneOffOpen] = useState(false);
  const [oneOffForm, setOneOffForm] = useState({
    client_id: "",
    description: "",
    hours: "",
    hourly_rate: "30",
    amount: "",
    currency: "EUR",
    month: selectedMonth,
  });
  const [oneOffSubmitting, setOneOffSubmitting] = useState(false);

  // Bill-to (client billing profile) picker — closes backlog 18168a9f.
  // Studio FOP is now picked at the client level (see above); this dialog
  // covers the *other* legal entity that wasn't pickable: the **client's**
  // legal entity (TOV/FOP) that the invoice is issued to (Otrymuvach).
  // Stored per-payment on sales_service.client_billing_profile_id; backend
  // resolves billing_profile_id query → service field → client default.
  const [billToOpen, setBillToOpen] = useState(false);
  const [billToService, setBillToService] = useState(null);
  const [billToClient, setBillToClient] = useState(null); // full sales-client doc with billing_profiles[]
  const [billToLoading, setBillToLoading] = useState(false);
  const [billToValue, setBillToValue] = useState("default");
  const [billToSaving, setBillToSaving] = useState(false);

  // Manual bank-transaction linking (mirror of CRM Income). Picker is filtered
  // server-side by payer name / ЕДРПОУ of the client.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkingService, setLinkingService] = useState(null);
  const [matchableTxs, setMatchableTxs] = useState([]);
  const [loadingTxs, setLoadingTxs] = useState(false);

  const openBillToDialog = async (service) => {
    setBillToService(service);
    setBillToValue(service.client_billing_profile_id || "default");
    setBillToClient(null);
    setBillToOpen(true);
    if (service.client_id) {
      setBillToLoading(true);
      try {
        const res = await salesClientsApi.get(service.client_id);
        setBillToClient(res.data || null);
      } catch (e) {
        console.warn("[support-bill-to] failed to load client", e);
      } finally {
        setBillToLoading(false);
      }
    }
  };

  const handleSaveBillTo = async () => {
    if (!billToService) return;
    setBillToSaving(true);
    try {
      const profiles = billToClient?.billing_profiles || [];
      const profile = billToValue === "default"
        ? null
        : profiles.find((p) => p.id === billToValue);
      await api.put(`/sales-services/${billToService.id}`, {
        client_billing_profile_id: profile?.id || null,
        client_billing_profile_label: profile?.label || profile?.legal_name || "",
      });
      toast.success("Bill-to saved");
      setBillToOpen(false);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join(" · ")
          : "Failed to save";
      toast.error(msg);
    } finally {
      setBillToSaving(false);
    }
  };

  const openLinkDialog = async (service) => {
    setLinkingService(service);
    setMatchableTxs([]);
    setLoadingTxs(true);
    setLinkOpen(true);
    try {
      const res = await api.get(
        `/bank-transactions/matchable?payment_type=support&payment_id=${service.id}`
      );
      setMatchableTxs(res.data.items || []);
    } catch (e) {
      console.error("[support-link] failed to load transactions", e);
      toast.error("Не удалось загрузить транзакции");
      setMatchableTxs([]);
    } finally {
      setLoadingTxs(false);
    }
  };

  const handleLinkTx = async (transactionId) => {
    if (!linkingService) return;
    try {
      await api.post(
        `/support-services/${linkingService.id}/link-transaction?transaction_id=${transactionId}`
      );
      toast.success("Платёж связан с банковской транзакцией");
      setLinkOpen(false);
      setLinkingService(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка при связывании");
    }
  };

  const handleUnlinkTx = async (serviceId) => {
    try {
      await api.delete(`/support-services/${serviceId}/link-transaction`);
      toast.success("Связь с транзакцией удалена");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка при удалении связи");
    }
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [svcRes, reqRes, fopRes] = await Promise.all([
        api.get(`/support-services?month=${selectedMonth}`),
        api.get(`/client-requests`).catch(() => ({ data: [] })),
        fopSettingsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setServices(svcRes.data || []);
      setFops((fopRes.data || []).filter((f) => f.is_active !== false));
      const requests = (reqRes.data || []).filter((r) => r.request_type === "payment_request");
      setClientRequests(requests);
      const sent = new Set();
      for (const r of requests) {
        if (r.service_id) sent.add(r.service_id);
      }
      setSentRequests(sent);
    } catch (e) {
      toast.error("Failed to load support payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const refresh = () => fetchData();

  // Inline FOP picker — set the issuing legal entity directly on the service
  // so the invoice can be generated without bouncing to the Clients tab.
  // FOP is the invoice issuer (Постачальник); picking the wrong entity is a
  // billing decision, so we never auto-assign — the manager chooses here.
  // (backlog 359a962f)
  const handleSetServiceFop = async (service, fopId) => {
    try {
      await api.put(`/sales-services/${service.id}`, { legal_entity_id: fopId || null });
      toast.success(fopId ? "FOP set" : "FOP cleared");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set FOP");
    }
  };

  // === Action handlers (copy-equivalent of SalesProjectEditPage flow) ===

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Invoice FOP + currency are client-level fields (managed in the Clients
  // tab Edit dialog). Generate Month copies them onto each sales_service,
  // so generating the DOCX is a single click — no extra picker.
  const handleDownloadInvoice = async (service) => {
    if (!service.legal_entity_id) {
      toast.error(
        `No FOP set for ${service.client_name || "this client"}. Open Clients tab → Edit → pick a FOP first.`,
        { duration: 8000 }
      );
      return;
    }
    try {
      toast.info("Generating invoice…");
      // Pass billing_profile_id explicitly when service has one set —
      // backend already falls back to it from sales_service.client_billing_profile_id,
      // this is just defensive for clarity.
      const params = new URLSearchParams({ format: "docx" });
      if (service.client_billing_profile_id) {
        params.set("billing_profile_id", service.client_billing_profile_id);
      }
      const response = await api.get(`/sales-services/${service.id}/invoice?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const safeName = (service.name || "Invoice").replace(/[^a-zA-Z0-9]/g, "_");
      downloadBlob(blob, `Invoice_${safeName}_${service.month || ""}.docx`);
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate invoice");
    }
  };

  const handleDownloadAct = async (service) => {
    try {
      toast.info("Generating act...");
      const params = new URLSearchParams({ format: "docx" });
      if (service.client_billing_profile_id) {
        params.set("billing_profile_id", service.client_billing_profile_id);
      }
      const response = await api.get(`/sales-services/${service.id}/act?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const safeName = (service.name || "Act").replace(/[^a-zA-Z0-9]/g, "_");
      downloadBlob(blob, `Act_${safeName}_${service.month || ""}.docx`);
      toast.success("Act downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate act");
    }
  };

  // Send Demand: optimistic icon flip + backend dedup guarantee.
  // We mark the service as sent BEFORE the API call so the icon switches
  // to CheckCircle2 instantly — eliminates the double-click bug where
  // users hit Send twice because the visual lagged. Backend is also
  // idempotent (returns existing pending demand instead of creating
  // a duplicate), so even if the optimistic flip races, no dup is written.
  const handleSendPaymentRequest = async (service) => {
    if (!service.client_id) {
      toast.error("Client not linked — open the client dialog and re-save");
      return;
    }
    if (sentRequests.has(service.id)) {
      // Already sent in this session — guard against accidental double-click
      toast.info("Demand already sent for this payment");
      return;
    }
    const amount = service.amount;
    const currency = service.currency || "EUR";
    // Optimistic flip — set BEFORE await so re-render happens immediately
    setSentRequests((prev) => new Set([...prev, service.id]));
    try {
      const res = await api.post("/client-requests", {
        client_id: service.client_id,
        project_id: service.project_id,
        service_id: service.id,
        request_type: "payment_request",
        title: `Payment Request: ${service.name}`,
        description: `Please process payment of ${amount?.toLocaleString() || 0} ${currency} for ${service.name}`,
        ball_in_court: "client",
        expected_action: "payment",
        payment_amount: amount,
        payment_currency: currency,
      });
      const sender = res.data?.created_by_name || "you";
      const when = res.data?.created_at?.slice(0, 10) || "today";
      toast.success(`Demand sent by ${sender} on ${when}`);
      refresh();
    } catch (e) {
      // Rollback optimistic flip on failure
      setSentRequests((prev) => {
        const next = new Set(prev);
        next.delete(service.id);
        return next;
      });
      toast.error(e.response?.data?.detail || "Failed to send demand");
    }
  };

  const handleRevokePaymentRequest = async (service) => {
    const existing = clientRequests.find((r) => r.service_id === service.id);
    if (!existing) return;
    if (!window.confirm(
      "Cancel the payment demand sent to this client? The invoice will be marked as not sent again and the client will lose access to it on their portal."
    )) return;
    try {
      await api.delete(`/client-requests/${existing.id}`);
      setSentRequests((prev) => {
        const next = new Set(prev);
        next.delete(service.id);
        return next;
      });
      toast.success("Demand revoked");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke demand");
    }
  };

  const handleCreateStripeLink = async (service) => {
    try {
      const res = await api.post("/stripe/create-payment-link", {
        service_id: service.id,
        origin_url: window.location.origin,
      });
      const url = res.data.checkout_url;
      await navigator.clipboard.writeText(url);
      toast.success("Stripe link copied to clipboard!");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create Stripe link");
    }
  };

  const handleMarkPaid = async (service) => {
    try {
      await api.put(`/sales-services/${service.id}`, {
        is_paid: true,
        paid_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Marked paid");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark paid");
    }
  };

  const handleDeleteService = async (service) => {
    if (!window.confirm(`Delete payment "${service.name}"? This is irreversible.`)) return;
    try {
      await api.delete(`/sales-services/${service.id}`);
      toast.success("Payment deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  // Open the one-off charge dialog. Defaults sensible: hours+rate driven,
  // €30/hr starting (pay-as-you-go tariff baseline), currency follows the
  // first selected client.
  const openOneOff = () => {
    setOneOffForm({
      client_id: oneOffClientPool?.[0]?.id || "",
      description: "",
      hours: "",
      hourly_rate: "30",
      amount: "",
      currency: oneOffClientPool?.[0]?.invoice_currency || "EUR",
      month: selectedMonth,
    });
    setOneOffOpen(true);
  };

  // Computed total: hours × rate (if provided) overrides the manual amount.
  const oneOffComputedAmount = useMemo(() => {
    const h = parseFloat(oneOffForm.hours);
    const r = parseFloat(oneOffForm.hourly_rate);
    if (h > 0 && r > 0) return +(h * r).toFixed(2);
    const a = parseFloat(oneOffForm.amount);
    return a > 0 ? a : 0;
  }, [oneOffForm.hours, oneOffForm.hourly_rate, oneOffForm.amount]);

  const submitOneOff = async () => {
    if (!oneOffForm.client_id) {
      toast.error("Pick a client.");
      return;
    }
    if (oneOffComputedAmount <= 0) {
      toast.error("Enter hours × rate, or a direct amount.");
      return;
    }
    setOneOffSubmitting(true);
    try {
      const payload = {
        client_id: oneOffForm.client_id,
        amount: oneOffComputedAmount,
        currency: oneOffForm.currency,
        description: oneOffForm.description?.trim() || null,
        month: oneOffForm.month || selectedMonth,
      };
      const h = parseFloat(oneOffForm.hours);
      const r = parseFloat(oneOffForm.hourly_rate);
      if (h > 0 && r > 0) {
        payload.hours = h;
        payload.hourly_rate = r;
      }
      await api.post("/support-services/one-off", payload);
      toast.success("One-off charge created");
      setOneOffOpen(false);
      refresh();
      if (typeof onAfterOneOff === "function") onAfterOneOff();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create one-off charge");
    } finally {
      setOneOffSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="support-payments-view">
      {/* Month navigator */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => onNavigateMonth(-1)} className="text-zinc-400 hover:text-zinc-50">
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <h2 className="text-lg font-semibold text-zinc-50 min-w-[140px] text-center">
            {fmtMonth(selectedMonth)}
          </h2>
          <Button variant="ghost" onClick={() => onNavigateMonth(1)} className="text-zinc-400 hover:text-zinc-50">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {supportClients && supportClients.length > 0 && (
            <Button
              onClick={openOneOff}
              variant="outline"
              className="border-amber-700/50 text-amber-300 hover:bg-amber-500/10"
            >
              <Plus className="h-4 w-4 mr-2" /> Add One-off
            </Button>
          )}
          {onGenerateMonth && (
            <Button
              onClick={async () => {
                await onGenerateMonth();
                refresh();
              }}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Generate {fmtMonth(selectedMonth)}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Client</TableHead>
              <TableHead className="text-zinc-400">Service</TableHead>
              <TableHead className="text-zinc-400 text-right">Amount</TableHead>
              <TableHead className="text-zinc-400">FOP</TableHead>
              <TableHead className="text-zinc-400">Inv. ccy</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-zinc-400">Invoice</TableHead>
              <TableHead className="text-zinc-400 w-[220px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-zinc-500">Loading…</TableCell></TableRow>
            ) : services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-zinc-500">
                  <p>No support payments for {fmtMonth(selectedMonth)}</p>
                  <p className="text-xs text-zinc-600 mt-1">
                    Click "Generate {fmtMonth(selectedMonth)}" to create monthly invoices for all active support clients.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              services.map((svc) => {
                const isPaid = !!svc.is_paid;
                const invoiceSent = !!svc.invoice_sent_at;
                const requestAlreadySent =
                  sentRequests.has(svc.id) ||
                  clientRequests.some((r) => r.service_id === svc.id);
                const breakdown = svc.support_breakdown;
                const hasBreakdown = !!(breakdown && breakdown.lines && breakdown.lines.length > 0);
                const isExpanded = expanded.has(svc.id);
                return (
                  <React.Fragment key={svc.id}>
                  <TableRow className="border-zinc-800 hover:bg-zinc-800/40">
                    <TableCell className="text-zinc-50 font-medium">
                      {svc.client_name || "—"}
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm">
                      <div className="flex items-center gap-2">
                        {hasBreakdown && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(svc.id)}
                            className="text-zinc-500 hover:text-zinc-300"
                            title={isExpanded ? "Hide breakdown" : "Show breakdown"}
                          >
                            {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                        )}
                        <span>{svc.name}</span>
                      </div>
                      {hasBreakdown && breakdown.extra_sites > 0 && (
                        <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
                          <Globe className="h-3 w-3 text-sky-400" />
                          {breakdown.sites_count} sites ({breakdown.included_sites} included
                          {" "}+ {breakdown.extra_sites} extra × {fmtCurrency(breakdown.extra_unit_price, breakdown.extra_currency)})
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-emerald-300 font-mono">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{fmtCurrency(svc.amount, svc.currency)}</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs">
                            <p>Saved as {fmtCurrencyRaw(svc.amount, svc.currency)} ({svc.currency})</p>
                            {svc.exchange_rate ? (
                              <p className="text-zinc-400">FX rate at issue: {Number(svc.exchange_rate).toFixed(4)}</p>
                            ) : null}
                            <p className="text-zinc-500 mt-1">Invoice DOCX uses the saved value.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={svc.legal_entity_id || "__none__"}
                        onValueChange={(v) =>
                          handleSetServiceFop(svc, v === "__none__" ? "" : v)
                        }
                      >
                        <SelectTrigger
                          className={cn(
                            "h-7 w-[150px] border-0 bg-transparent px-1 text-xs hover:bg-zinc-800/60",
                            svc.legal_entity_id ? "text-zinc-300" : "text-amber-500/70 italic"
                          )}
                          title={svc.legal_entity_id ? "Change FOP" : "No FOP — pick the issuing entity"}
                        >
                          <span className="inline-flex items-center gap-1 truncate">
                            <Building2 className="h-3 w-3 text-sky-400 shrink-0" />
                            {fops.find((f) => f.id === svc.legal_entity_id)?.name || "Not set"}
                          </span>
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          <SelectItem value="__none__" className="text-zinc-500">— None —</SelectItem>
                          {fops.map((f) => (
                            <SelectItem key={f.id} value={f.id} className="text-zinc-50">
                              {f.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {svc.currency ? (
                        <Badge className="bg-zinc-700/40 text-zinc-300 border-zinc-700 text-xs">
                          {svc.currency}
                        </Badge>
                      ) : (
                        <span className="text-xs text-zinc-600">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                      {isPaid ? (
                        svc.bank_transaction_info ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-500/50 cursor-help inline-flex items-center gap-1 whitespace-nowrap">
                                  <Landmark className="h-3 w-3 shrink-0" />
                                  Paid from bank
                                  {svc.bank_transaction_info.auto_matched && (
                                    <span className="text-emerald-400/70 text-[10px] ml-0.5">auto</span>
                                  )}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs max-w-xs">
                                <p className="font-semibold text-emerald-300">
                                  {svc.bank_transaction_info.amount?.toLocaleString()} {svc.bank_transaction_info.currency}
                                </p>
                                <p>Date: {svc.bank_transaction_info.date || svc.paid_date}</p>
                                {svc.bank_transaction_info.counterparty_name && (
                                  <p>From: {svc.bank_transaction_info.counterparty_name}</p>
                                )}
                                {svc.bank_transaction_info.purpose && (
                                  <p className="text-zinc-400 mt-1 italic">"{svc.bank_transaction_info.purpose.slice(0, 100)}"</p>
                                )}
                                {svc.bank_transaction_info.auto_matched && (
                                  <p className="text-emerald-400 mt-1">Auto-matched by invoice number</p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 cursor-help">Paid</Badge>
                              </TooltipTrigger>
                              <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs max-w-xs">
                                <p className="font-semibold text-emerald-300">
                                  {svc.payment_method === "stripe" ? "Оплачено через Stripe" : "Отмечено оплаченным вручную"}
                                </p>
                                {svc.paid_date && <p>Дата: {svc.paid_date}</p>}
                                {svc.paid_by_name && <p>Кем: {svc.paid_by_name}</p>}
                                <p className="text-amber-300/80 mt-1">
                                  Не привязано к банковской транзакции — нажмите «Связать», чтобы сверить с поступлением.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )
                      ) : invoiceSent ? (
                        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">Invoiced</Badge>
                      ) : (
                        <Badge className="bg-zinc-700/40 text-zinc-300 border-zinc-700">Planned</Badge>
                      )}
                      {svc.bank_transaction_info ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUnlinkTx(svc.id)}
                          className="h-6 w-6 p-0 text-emerald-400 hover:text-rose-400 hover:bg-rose-500/10"
                          title="Отвязать банковскую транзакцию"
                        >
                          <Landmark className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openLinkDialog(svc)}
                          className="h-6 px-1.5 gap-1 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                          title="Связать с прошедшим платежом"
                        >
                          <Landmark className="h-3.5 w-3.5" />
                          <span className="text-[11px]">Связать</span>
                        </Button>
                      )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400 whitespace-nowrap">
                      {invoiceSent ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help whitespace-nowrap">
                                Sent {svc.invoice_sent_at?.slice(0, 10)}
                                {svc.invoice_due_date && (
                                  <span className="text-zinc-500"> · due {svc.invoice_due_date.slice(0, 10)}</span>
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs">
                              <p>By: {svc.invoice_sent_by_name || svc.invoice_sent_by || "—"}</p>
                              <p>To: {svc.invoice_sent_to || "client"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5 flex-nowrap">
                        {/* Bill-to (client legal entity / billing profile) —
                            sky chip when set, amber when client has profiles
                            but none selected, hidden when client has none.
                            Closes backlog 18168a9f. */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openBillToDialog(svc)}
                          className={cn(
                            "h-7 w-7 p-0",
                            svc.client_billing_profile_id
                              ? "text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
                              : "text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10"
                          )}
                          title={
                            svc.client_billing_profile_label
                              ? `Bill to: ${svc.client_billing_profile_label} (click to change)`
                              : "Pick client legal entity (Bill to)"
                          }
                        >
                          <Building2 className="h-3.5 w-3.5" />
                        </Button>

                        {/* Generate invoice — uses FOP + currency from client */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadInvoice(svc)}
                          className="h-7 w-7 p-0 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                          title="Download invoice DOCX"
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </Button>

                        {/* Download act */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAct(svc)}
                          className="h-7 w-7 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          title="Download Act (work-completion document)"
                        >
                          <FileCheck className="h-3.5 w-3.5" />
                        </Button>

                        {/* Demand payment — single button. Click sends; click-on-sent revokes.
                            Visual when sent: SOLID emerald fill + white CheckCircle2 + ring + tooltip showing
                            sender + date. Visual when unsent: amber outline Send icon.
                            Made unmistakable per 2026-05-22 user feedback (was getting double-sent
                            because the prior subtle bg-emerald-500/15 looked similar to amber on dark theme). */}
                        {(() => {
                          const linkedRequest = clientRequests.find((r) => r.service_id === svc.id);
                          const sentDate = linkedRequest?.created_at?.slice(0, 10);
                          const sender = linkedRequest?.created_by_name || "—";
                          return (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => requestAlreadySent ? handleRevokePaymentRequest(svc) : handleSendPaymentRequest(svc)}
                              className={cn(
                                "h-7 w-7 p-0 transition-all",
                                requestAlreadySent
                                  ? "bg-emerald-500 text-white hover:bg-rose-500 hover:text-white ring-2 ring-emerald-300/70 shadow-md shadow-emerald-500/30"
                                  : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                              )}
                              title={requestAlreadySent
                                ? `✓ DEMAND SENT\nBy: ${sender}\nOn: ${sentDate || "today"}\n(click to revoke)`
                                : "Send payment demand to client"}
                            >
                              {requestAlreadySent ? (
                                <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                              ) : (
                                <Send className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          );
                        })()}

                        {/* Stripe */}
                        {svc.stripe_payment_status === "paid" ? (
                          <span
                            className="h-7 w-7 flex items-center justify-center text-green-400"
                            title="Stripe paid"
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                          </span>
                        ) : svc.stripe_checkout_url ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCreateStripeLink(svc)}
                            className="h-7 w-7 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                            title="Copy Stripe payment link"
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCreateStripeLink(svc)}
                            className="h-7 w-7 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                            title="Create Stripe payment link"
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                          </Button>
                        )}

                        {/* Mark paid (manual) — only when not yet paid */}
                        {!isPaid && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkPaid(svc)}
                            className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            title="Mark as paid manually"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                        )}

                        {/* Open payment detail */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(`/sales/payment/${svc.id}`, "_blank")}
                          className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-50"
                          title="Open payment details in new tab"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>

                        {/* Delete */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteService(svc)}
                          className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300"
                          title="Delete payment"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {hasBreakdown && isExpanded && (
                    <TableRow className="border-zinc-800 bg-zinc-950/40">
                      <TableCell colSpan={8} className="py-3 pl-12">
                        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                          Invoice breakdown
                        </div>
                        <table className="text-sm w-full max-w-[640px]">
                          <thead>
                            <tr className="border-b border-zinc-800/50 text-zinc-500 text-xs">
                              <th className="text-left py-1.5 font-normal">Line item</th>
                              <th className="text-right py-1.5 font-normal w-16">Qty</th>
                              <th className="text-right py-1.5 font-normal w-28">Unit</th>
                              <th className="text-right py-1.5 font-normal w-28">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {breakdown.lines.map((line, idx) => (
                              <tr key={idx} className="border-b border-zinc-800/30">
                                <td className="py-1.5 text-zinc-300">{line.label}</td>
                                <td className="text-right text-zinc-400 font-mono">{line.qty}</td>
                                <td className="text-right text-zinc-400 font-mono">{fmtCurrency(line.unit_price, line.currency)}</td>
                                <td className="text-right text-emerald-300 font-mono">{fmtCurrency(line.line_total, line.currency)}</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan={3} className="py-1.5 text-right text-zinc-400 font-semibold">Total</td>
                              <td className="py-1.5 text-right text-emerald-400 font-mono font-bold">{fmtCurrency(svc.amount, svc.currency)}</td>
                            </tr>
                          </tbody>
                        </table>
                        {breakdown.currency_mismatch && (
                          <div className="text-xs text-amber-400 mt-2">
                            ⚠ Extra-site currency differs from tariff currency — total may need manual review.
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* One-off charge dialog (pay-as-you-go tariff / ad-hoc support work) */}
      <Dialog open={oneOffOpen} onOpenChange={setOneOffOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Plus className="h-5 w-5 text-amber-400" />
              Add One-off Support Charge
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Client *</Label>
              <Select
                value={oneOffForm.client_id || ""}
                onValueChange={(v) => {
                  const c = (oneOffClientPool || []).find((x) => x.id === v);
                  setOneOffForm((p) => ({
                    ...p,
                    client_id: v,
                    currency: c?.invoice_currency || c?.currency || p.currency,
                  }));
                }}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Pick a client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 max-h-72">
                  {(oneOffClientPool || []).map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-zinc-50">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Description (shows on invoice)</Label>
              <Input
                value={oneOffForm.description}
                onChange={(e) => setOneOffForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="e.g. Urgent fix on landing — 12 May"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Hours</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  value={oneOffForm.hours}
                  onChange={(e) => setOneOffForm((p) => ({ ...p, hours: e.target.value }))}
                  placeholder="2"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Hourly rate</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={oneOffForm.hourly_rate}
                  onChange={(e) => setOneOffForm((p) => ({ ...p, hourly_rate: e.target.value }))}
                  placeholder="30"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">
                Or direct amount (overridden when hours × rate is set)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={oneOffForm.amount}
                onChange={(e) => setOneOffForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0"
                disabled={!!(parseFloat(oneOffForm.hours) > 0 && parseFloat(oneOffForm.hourly_rate) > 0)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 disabled:opacity-50"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Currency</Label>
                <Select
                  value={oneOffForm.currency}
                  onValueChange={(v) => setOneOffForm((p) => ({ ...p, currency: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                    <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                    <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Month</Label>
                <Input
                  type="month"
                  value={oneOffForm.month}
                  onChange={(e) => setOneOffForm((p) => ({ ...p, month: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>

            <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm">
              <span className="text-zinc-400 text-xs uppercase tracking-wider mr-2">Total</span>
              <span className="text-emerald-300 font-mono font-bold">
                {fmtCurrencyRaw(oneOffComputedAmount, oneOffForm.currency)}
              </span>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOneOffOpen(false)}
              className="border-zinc-700"
              disabled={oneOffSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitOneOff}
              disabled={oneOffSubmitting || oneOffComputedAmount <= 0 || !oneOffForm.client_id}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {oneOffSubmitting ? "Creating…" : "Create charge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill-to (client legal entity) picker — closes backlog 18168a9f.
          PM_lead reported missing UI to pick which of the client's legal
          entities (billing_profiles[]) is the invoice recipient on
          /support-payments. */}
      <Dialog open={billToOpen} onOpenChange={setBillToOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              Bill to (Client Legal Entity)
            </DialogTitle>
            {billToService && (
              <p className="text-xs text-zinc-500 mt-1">
                {billToService.client_name} — {billToService.name}
              </p>
            )}
          </DialogHeader>

          <div className="py-2 space-y-3">
            {billToLoading ? (
              <div className="text-sm text-zinc-500">Loading client billing profiles…</div>
            ) : (() => {
              const profiles = billToClient?.billing_profiles || [];
              if (profiles.length === 0) {
                return (
                  <div className="text-sm text-amber-400">
                    This client has no billing profiles configured yet.
                    {billToService?.client_id && (
                      <>
                        {" "}Open the{" "}
                        <a
                          className="underline hover:text-amber-300"
                          href={`/clients?focus=${billToService.client_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          client edit page
                        </a>
                        {" "}→ Billing tab to add legal name, EDRPOU, IBAN…
                      </>
                    )}
                  </div>
                );
              }
              const defaultProfile = profiles.find((p) => p.is_default) || profiles[0];
              // Identify a profile by its real legal identity, never a bare
              // "Default" label (the auto-migrated label many clients carry).
              const profileLabel = (p) => {
                const ln = (p?.legal_name || "").trim();
                if (ln) return p.edrpou ? `${ln} · EDRPOU ${p.edrpou}` : ln;
                const lb = (p?.label || "").trim();
                if (lb && lb.toLowerCase() !== "default") {
                  return p.edrpou ? `${lb} · EDRPOU ${p.edrpou}` : lb;
                }
                if (p?.edrpou) return `EDRPOU ${p.edrpou}`;
                return "(unnamed profile)";
              };
              const defaultName = profileLabel(defaultProfile);
              // Single legal entity → there is nothing to pick. Show it read-only
              // instead of a misleading "Default / Default" two-option dropdown.
              if (profiles.length === 1) {
                return (
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Recipient on invoice and act</Label>
                    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-2 text-sm text-zinc-200">
                      {profileLabel(profiles[0])}
                    </div>
                    <p className="text-xs text-zinc-500">
                      Single billing entity — every invoice and act is addressed to
                      it. Add more legal entities on the client's Billing tab to
                      choose between them here.
                    </p>
                  </div>
                );
              }
              return (
                <>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Recipient on invoice and act</Label>
                    <Select value={billToValue} onValueChange={setBillToValue}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="default" className="text-zinc-300">
                          Client default{defaultName && defaultName !== "(unnamed profile)" ? ` — ${defaultName}` : ""}
                        </SelectItem>
                        {profiles.map((p) => (
                          <SelectItem key={p.id} value={p.id} className="text-zinc-50">
                            {profileLabel(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500">
                      Used as the recipient on the invoice and act. "Client default"
                      lets the backend pick the profile marked default for this client.
                    </p>
                  </div>
                  {billToValue !== "default" && (() => {
                    const p = profiles.find((x) => x.id === billToValue);
                    if (!p) return null;
                    return (
                      <div className="rounded border border-zinc-800 bg-zinc-950/40 p-2 text-xs text-zinc-400 space-y-0.5">
                        {p.legal_name && <div><span className="text-zinc-500">Legal name:</span> <span className="text-zinc-300">{p.legal_name}</span></div>}
                        {p.edrpou && <div><span className="text-zinc-500">EDRPOU:</span> <span className="text-zinc-300">{p.edrpou}</span></div>}
                        {p.iban && <div><span className="text-zinc-500">IBAN:</span> <span className="text-zinc-300 font-mono text-[11px]">{p.iban}</span></div>}
                        {p.bank_name && <div><span className="text-zinc-500">Bank:</span> <span className="text-zinc-300">{p.bank_name}</span></div>}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setBillToOpen(false)}
              className="text-zinc-400 hover:text-zinc-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveBillTo}
              disabled={billToSaving || billToLoading || !(billToClient?.billing_profiles || []).length}
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              {billToSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link a support payment to an already-received bank transaction.
          Picker is filtered server-side by payer name / ЕДРПОУ of the client. */}
      <Dialog
        open={linkOpen}
        onOpenChange={(open) => {
          if (!open) {
            setLinkOpen(false);
            setLinkingService(null);
            setMatchableTxs([]);
          }
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Landmark className="h-5 w-5 text-cyan-400" />
              Связать с банковской транзакцией
            </DialogTitle>
            {linkingService && (
              <p className="text-xs text-zinc-500">
                {linkingService.client_name} · {linkingService.name}
              </p>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingTxs ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-zinc-400">Загрузка транзакций...</div>
              </div>
            ) : matchableTxs.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Landmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Нет несвязанных транзакций по этому плательщику</p>
                <p className="text-xs mt-1">Совпадение ищется по названию плательщика, ЕДРПОУ клиента или сумме платежа</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500 mb-3">
                  Найдено {matchableTxs.length} транзакций по плательщику / ЕДРПОУ / сумме. Выберите нужную (<span className="text-amber-400">по сумме</span> — совпало только по сумме, проверьте плательщика):
                </p>
                {matchableTxs.map((tx) => (
                  <div
                    key={tx.id}
                    className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-cyan-600 cursor-pointer transition-colors"
                    onClick={() => handleLinkTx(tx.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{tx.date}</Badge>
                          <Badge className="bg-blue-900/30 text-blue-400 text-[10px]">{tx.fop_name || "FOP"}</Badge>
                          {tx.match_reason === "amount" && (
                            <Badge className="bg-amber-900/30 text-amber-400 text-[10px]">по сумме</Badge>
                          )}
                        </div>
                        <p className="text-zinc-50 text-sm font-medium">
                          {tx.counterparty_name || "—"}
                          {tx.counterparty_edrpou && tx.counterparty_edrpou !== "000000000" && (
                            <span className="text-zinc-500 font-normal text-xs ml-2">ЕДРПОУ {tx.counterparty_edrpou}</span>
                          )}
                        </p>
                        <p className="text-zinc-400 text-xs truncate max-w-md" title={tx.purpose}>
                          {tx.purpose?.substring(0, 80) || "—"}{tx.purpose?.length > 80 ? "..." : ""}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-green-400 font-mono font-medium">
                          +{tx.amount?.toLocaleString()} {tx.currency}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-zinc-800 pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setLinkOpen(false);
                setLinkingService(null);
              }}
              className="border-zinc-700"
            >
              Отмена
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
